import { DatePipe } from '@angular/common'
import { Component, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute, RouterLink } from '@angular/router'
import { NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase } from '@ng-bootstrap/ng-bootstrap'

import {
    BaseComponent,
    FeatureType,
    SwpppActionItem,
    SwpppDocument,
    SwpppInspectionAssignment,
    SwpppSiteBundle,
    SwpppSitePerson,
} from '../../shared'
import { SwpppMapComponent } from './swppp-map.component'

interface LookupUser {
    id: string
    displayName: string
    active: boolean
}

@Component({
    selector: 'app-swppp-site',
    templateUrl: './swppp-site.component.html',
    standalone: true,
    imports: [DatePipe, FormsModule, RouterLink, NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, SwpppMapComponent],
})
export class SwpppSiteComponent extends BaseComponent implements OnInit {
    bundle?: SwpppSiteBundle
    users: LookupUser[] = []
    forms: { id: string; name: string }[] = []
    tab = 0
    editingPersonId = ''
    person: Partial<SwpppSitePerson> = this.blankPerson()
    assignment: Partial<SwpppInspectionAssignment> = this.blankAssignment()
    assignmentUserIds: string[] = []
    reminderType = 'InspectionLatenessPreWarning'
    reminderDays = 1
    document: Partial<SwpppDocument> = this.blankDocument()
    documentFile?: File
    action: Partial<SwpppActionItem> = { description: '', status: 'Open' }
    noteText = ''

    constructor(private route: ActivatedRoute) {
        super()
    }

    ngOnInit() {
        this.services.data.users.queryAllByAction('GetForLookup').subscribe(users => {
            this.users = ((users as LookupUser[]) ?? []).filter(user => user.active)
        })
        this.services.data.swpppForms.queryAll().subscribe(forms => {
            this.forms = forms
            if (!this.assignment.formId && forms[0]) this.assignment.formId = forms[0].id
        })
        this.route.paramMap.subscribe(params => {
            const id = params.get('id')
            if (id) this.load(id)
        })
    }

    load(id: string) {
        this.services.data.get<SwpppSiteBundle>('/api/SwpppSite/GetBundle?id=' + id).subscribe({
            next: bundle => {
                this.bundle = bundle
                this.initialLoadingComplete()
            },
            error: () => this.initialLoadingComplete(),
        })
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.FieldToolsSwpppEdit])
    }

    sourceUrl() {
        const site = this.bundle?.site
        if (!site) return ''
        return 'https://forecast.weather.gov/MapClick.php?lat=' + site.latitude + '&lon=' + site.longitude
    }

    qrUrl() {
        return '/api/SwpppSite/QrCode?id=' + this.bundle?.site.id
    }

    fileUrl(file: { id: string }) {
        return '/api/SwpppDocument/DownloadDocument?documentId=' + file.id
    }

    startInspection(dueId: string) {
        this.services.data.post<{ inspection: { id: string } }>('/api/SwpppInspection/Start?dueId=' + dueId, {}).subscribe({
            next: detail => this.services.angular.router.navigate(['/field-tools/swppp/inspection', detail.inspection.id]),
            error: error => this.fail(error, 'The inspection could not be started.'),
        })
    }

    startOneOff() {
        if (!this.bundle) return
        this.services.data.post<{ inspection: { id: string } }>('/api/SwpppInspection/StartOneOff?siteId=' + this.bundle.site.id, {}).subscribe({
            next: detail => this.services.angular.router.navigate(['/field-tools/swppp/inspection', detail.inspection.id]),
            error: error => this.fail(error, 'The inspection could not be started.'),
        })
    }

    editPerson(person: SwpppSitePerson) {
        this.editingPersonId = person.id
        this.person = { ...person }
    }

    cancelPerson() {
        this.editingPersonId = ''
        this.person = this.blankPerson()
    }

    savePerson() {
        if (!this.bundle) return
        if (this.editingPersonId) {
            const person = {
                ...this.person,
                id: this.editingPersonId,
                siteId: this.bundle.site.id,
            }
            this.services.data.swpppSitePeople.edit(person as SwpppSitePerson).subscribe(() => {
                this.cancelPerson()
                this.load(this.bundle!.site.id)
            })
            return
        }
        const person = {
            ...this.blankPerson(),
            ...this.person,
            id: crypto.randomUUID(),
            siteId: this.bundle.site.id,
            createdOn: new Date().toISOString(),
        }
        this.services.data.swpppSitePeople.add(person as SwpppSitePerson).subscribe(() => {
            this.cancelPerson()
            this.load(this.bundle!.site.id)
        })
    }

    removePerson(person: SwpppSitePerson) {
        if (!this.bundle) return
        const name = person.name || person.email || 'this person'
        this.services.modals.confirm('Remove person', `Remove ${name} from this site? They will no longer get SWPPP emails.`).then(
            () => {
                if (this.editingPersonId === person.id) this.cancelPerson()
                this.services.data.swpppSitePeople.delete(person).subscribe(() => this.load(this.bundle!.site.id))
            },
            () => undefined,
        )
    }

    saveAssignment() {
        if (!this.bundle) return
        if (!this.assignment.formId && this.forms[0]) this.assignment.formId = this.forms[0].id
        if (!this.assignment.formId) {
            this.services.notification.error('No inspection form is available. The SWPPP form has to be set up before an assignment can be saved.')
            return
        }
        const body = {
            assignment: {
                ...this.blankAssignment(),
                ...this.assignment,
                id: crypto.randomUUID(),
                siteId: this.bundle.site.id,
                createdOn: new Date().toISOString(),
            },
            userIds: this.assignmentUserIds,
            reminders: [{ type: this.reminderType, offsetValue: Number(this.reminderDays) || 1, offsetUnit: 'Days' }],
        }
        this.services.data.post('/api/SwpppInspectionAssignment/Save', body).subscribe({
            next: () => {
                this.assignment = this.blankAssignment()
                if (this.forms[0]) this.assignment.formId = this.forms[0].id
                this.assignmentUserIds = []
                this.load(this.bundle!.site.id)
            },
            error: error => this.fail(error, 'The assignment could not be saved.'),
        })
    }

    saveDocument() {
        if (!this.bundle) return
        const title = this.document.title?.trim()
        if (!title) {
            this.services.notification.error('Enter a title before adding the document.')
            return
        }
        const doc = {
            ...this.blankDocument(),
            ...this.document,
            title,
            effectiveDate: this.dateOrNull(this.document.effectiveDate),
            expirationDate: this.dateOrNull(this.document.expirationDate),
            renewedOn: this.dateOrNull(this.document.renewedOn),
            reminderDaysBefore: Number(this.document.reminderDaysBefore) > 0 ? Number(this.document.reminderDaysBefore) : 45,
            id: crypto.randomUUID(),
            siteId: this.bundle.site.id,
            createdOn: new Date().toISOString(),
        }
        this.services.data.swpppDocuments.add(doc as SwpppDocument).subscribe({
            next: saved => {
                if (!this.documentFile) {
                    this.document = this.blankDocument()
                    this.load(this.bundle!.site.id)
                    return
                }
                const data = new FormData()
                data.append('id', saved.id)
                data.append('myFile0', this.documentFile, this.documentFile.name)
                this.services.data.post('/api/SwpppDocument/UploadImages', data).subscribe({
                    next: () => {
                        this.document = this.blankDocument()
                        this.documentFile = undefined
                        this.load(this.bundle!.site.id)
                    },
                    error: error => this.fail(error, 'The document was added, but the file could not be uploaded.'),
                })
            },
            error: error => this.fail(error, 'The document could not be added.'),
        })
    }

    onDocumentFile(event: Event) {
        const input = event.target as HTMLInputElement
        this.documentFile = input.files?.[0]
    }

    saveAction() {
        if (!this.bundle) return
        const description = this.action.description?.trim()
        if (!description) {
            this.services.notification.error('Enter an action item before adding it.')
            return
        }
        this.services.data.swpppActionItems
            .add({
                id: crypto.randomUUID(),
                siteId: this.bundle.site.id,
                description,
                location: this.action.location,
                dueDate: this.dateOrNull(this.action.dueDate),
                status: 'Open',
                createdOn: new Date().toISOString(),
            } as SwpppActionItem)
            .subscribe({
                next: () => {
                    this.action = { description: '', status: 'Open' }
                    this.load(this.bundle!.site.id)
                },
                error: error => this.fail(error, 'The action item could not be added.'),
            })
    }

    verify(id: string) {
        this.services.data.post('/api/SwpppActionItem/Verify?id=' + id, {}).subscribe(() => this.load(this.bundle!.site.id))
    }

    saveNote() {
        if (!this.bundle || !this.noteText.trim()) return
        this.services.data.swpppNotes
            .add({ id: crypto.randomUUID(), siteId: this.bundle.site.id, text: this.noteText, createdOn: new Date().toISOString() } as any)
            .subscribe(() => {
                this.noteText = ''
                this.load(this.bundle!.site.id)
            })
    }

    setActive(isActive: boolean) {
        if (!this.bundle) return
        this.services.data.swpppSites.edit({ ...this.bundle.site, isActive } as any).subscribe(() => this.load(this.bundle!.site.id))
    }

    printQr() {
        window.print()
    }

    private blankPerson(): Partial<SwpppSitePerson> {
        return { role: 'Inspector', isPublicContact: false, receivesReports: true, reportPackage: 'Report', requiresActionSignoff: false }
    }

    private blankAssignment(): Partial<SwpppInspectionAssignment> {
        return {
            triggerType: 'Recurring',
            frequency: 'WeeklyAnyDay',
            precipThresholdInches: 0.5,
            startDate: new Date().toISOString(),
            includeWeatherHistory: false,
            allowSimultaneous: true,
            isActive: true,
        }
    }

    private blankDocument(): Partial<SwpppDocument> {
        return { docType: 'Permit', title: '', reminderDaysBefore: 45 }
    }

    private dateOrNull(value: unknown): string | null {
        if (value == null || value === '') return null
        if (value instanceof Date) {
            if (isNaN(value.getTime())) return null
            const pad = (n: number) => String(n).padStart(2, '0')
            return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`
        }
        const text = String(value).trim()
        return text || null
    }

    private fail(error: { error?: unknown }, fallback: string) {
        const body = error?.error
        let message = fallback
        if (typeof body === 'string' && body.trim() && !body.trim().startsWith('<')) message = body
        else if (body && typeof body === 'object') {
            const problem = body as { detail?: string; title?: string }
            if (problem.detail || problem.title) message = problem.detail || problem.title || fallback
        }
        this.services.notification.error(message)
    }
}
